import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch subscription on mount and when auth changes
  useEffect(() => {
    fetchSubscription();
    
    // Listen for auth changes (login/logout/register)
    const handleAuthChange = (e) => {
      if (e.detail?.type === 'logout') {
        setSubscription(null);
        setLoading(false);
      } else {
        // Slight delay to ensure token is saved
        setTimeout(() => fetchSubscription(), 100);
      }
    };
    
    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, [fetchSubscription]);

  const refreshSubscription = useCallback(async () => {
    setLoading(true);
    await fetchSubscription();
  }, [fetchSubscription]);

  const isTrialActive = subscription?.status === 'trial';
  const isSubscribed = subscription?.status === 'active';
  const isExpired = subscription?.status === 'expired' || subscription?.status === 'cancelled';

  const isMapLimitReached = isTrialActive && subscription?.mind_maps_limit > 0 &&
    subscription.mind_maps_used >= subscription.mind_maps_limit;

  const canUseMindMap = () => {
    if (!subscription) return false;
    if (isSubscribed) return true;
    if (isTrialActive) {
      return subscription.mind_maps_used < subscription.mind_maps_limit;
    }
    return false;
  };

  const canUseAIExplanation = () => {
    if (!subscription) return false;
    if (isSubscribed) return true;
    if (isTrialActive) {
      return subscription.ai_explanations_used < subscription.ai_explanations_limit;
    }
    return false;
  };

  const canCreateClub = () => {
    if (!subscription) return false;
    return subscription.can_create_clubs;
  };

  const getRemainingMindMaps = () => {
    if (!subscription) return 0;
    if (isSubscribed) return '∞';
    return Math.max(0, subscription.mind_maps_limit - subscription.mind_maps_used);
  };

  const getRemainingExplanations = () => {
    if (!subscription) return 0;
    if (isSubscribed) return '∞';
    return Math.max(0, subscription.ai_explanations_limit - subscription.ai_explanations_used);
  };

  const value = {
    subscription,
    loading,
    refreshSubscription,
    isTrialActive,
    isSubscribed,
    isExpired,
    isMapLimitReached,
    canUseMindMap,
    canUseAIExplanation,
    canCreateClub,
    getRemainingMindMaps,
    getRemainingExplanations
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
